import { useCallback, useRef } from 'preact/hooks';
import type { RefObject } from 'preact';
import { AM_VALUE, getAmPmMatchType, PM_VALUE } from '#utils/UNSAFE_timeUtils';
import type { AmPmLocalizedValues } from '#utils/UNSAFE_timeUtils';
import type { TimeReducerAction } from './timeReducer';

// A hook that returns handlers for input events that spread on the day period segment so that the user
// can type in a letter to toggle the day period (e.g., AM/PM).
export function useDayPeriodInputHandlers(
  amPm: AmPmLocalizedValues,
  ref: RefObject<HTMLDivElement>,
  onChange?: (action: TimeReducerAction) => void
) {
  // Used to record the current state of the element in beforeinput so we can restore it in the `input` event below, for inputType 'insertCompositionText'.
  const compositionRef = useRef<string | null>(null);

  const updateDayPeriodSegmentState = useCallback(
    (value: number) => {
      onChange?.({
        actionType: 'updateDayPeriodSegment',
        data: { type: 'dayPeriod', 'aria-valuenow': value }
      });
    },
    [onChange]
  );

  // This is useful if the AM/PM strings start with the same
  // letter. In that case when we get a letter that matches
  // the start of both, we simply toggle the dayPeriod.
  const toggleDayPeriodSegmentState = useCallback(() => {
    onChange?.({
      actionType: 'step',
      data: { type: 'dayPeriod', direction: 'increase' }
    });
  }, [onChange]);

  const changeDayPeriodIfMatch = useCallback(
    (data: string | null) => {
      if (data !== null && data.trim() !== '' && isLetterOnlyString(data)) {
        // In most locales the first letter of the am/pm strings uniquely identifies am or pm.
        // In a couple locales, such as 'ms' (where am is represented 'PG' and pm as 'PTG'), the first letter may not be unique.
        // In this case we toggle the day period if matchType === 'both'.
        const matchType = getAmPmMatchType(amPm, data);
        if (matchType === 'am' || matchType === 'pm') {
          const value = matchType === 'am' ? AM_VALUE : PM_VALUE;

          updateDayPeriodSegmentState(value);
        } else if (matchType === 'both') {
          toggleDayPeriodSegmentState();
        }
      }
    },
    [amPm, toggleDayPeriodSegmentState, updateDayPeriodSegmentState]
  );

  // inputType 'insertText': Android Samsung, ios, Mac, Windows.
  // inputType 'insertCompositionText': Input Method Editor (IME), such as Pinyin for Chinese, and Android Google.
  // 'insertCompositionText' represents immediate text that is part of an ongoing composition, meaning the user has not yet finalized their input.
  const handleBeforeInput = useCallback(
    (e: InputEvent) => {
      e.preventDefault();
      switch (e.inputType) {
        case 'insertCompositionText':
          // insertCompositionText cannot be cancelled so preventDefault does not keep the user's input from showing in the field.
          // (https://www.w3.org/TR/input-events-2/).
          // To workaround this, record the current state of the element so we can restore it in the `input` event below.
          if (ref.current) {
            compositionRef.current = getTextContentFilterOutAriaHidden(ref.current);
          }
          break;
        default:
          // Android Google never gets here, so we need to change the dayPeriod in handleInput's insertCompositionText.
          changeDayPeriodIfMatch(e.data);
          break;
      }
    },
    [changeDayPeriodIfMatch, ref]
  );

  const handleInput = useCallback(
    (e: InputEvent) => {
      const { inputType, data } = e;
      switch (inputType) {
        // we handle the 'insertText' case entirely in handleBeforeInput, but since you cannot cancel 'insertCompositionText' events,
        // we have to do some work here.
        case 'insertCompositionText':
          // Reset the DOM to how it was in the beforeinput event.
          // If we don't do this the user will see what they typed in multi-byte text like Chinese.
          if (ref.current && compositionRef.current !== null) {
            setVisibleText(ref.current, compositionRef.current);
          }
          // Android Google gets here, so this is where we need to check day period.
          changeDayPeriodIfMatch(data);
          break;
      }
    },
    [changeDayPeriodIfMatch, ref]
  );

  // NOTE: I could not get oncompositionend to be called when using Chinese or Korean IME keyboard.

  return {
    onBeforeInput: handleBeforeInput,
    onInput: handleInput
  };
}

const isLetterOnlyString = (input: string) => {
  // /u flag: This enables Unicode matching in Javascript regular expressions, allowing you to match Unicode characters properly,
  // including those outside of the ASCII range.
  // p{L}: This is a Unicode property escape, which matches any letter in any language, not just ASCII letters.
  // The L property refers to Letter characters, which includes
  // * Upper and lowercase letters in the Latin alphabet.
  // * Letters in non-Latin scripts (e.g., Cyrillic, Greek, Arabic, and also Chinese characters.)
  const isLetter = /^\p{L}$/u.test(input);

  return isLetter;
};

const getTextContentFilterOutAriaHidden = (elem: HTMLDivElement) => {
  const textContent = getVisibleTextNodes(elem)
    .map((node) => node.textContent)
    .join('');
  return textContent;
};

const setVisibleText = (elem: HTMLDivElement, newText: string) => {
  // there is always only one visible text node for the AM/PM segment.
  getVisibleTextNodes(elem)[0].textContent = newText;
};

const getVisibleTextNodes = (elem: HTMLDivElement) => {
  return Array.from(elem.childNodes).filter(
    (node) =>
      !(
        node.nodeType === Node.ELEMENT_NODE &&
        (node as HTMLElement).getAttribute('aria-hidden') === 'true'
      )
  );
};
